// src/context/SettingsContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useUser } from './UserContext';
import { getSettings, saveSettings } from '../services/firestore';

const SettingsContext = createContext();

export function useSettings() {
  return useContext(SettingsContext);
}

export function SettingsProvider({ children }) {
  const currentYear = new Date().getFullYear();
  const { user } = useUser();
  
  const [globalSettings, setGlobalSettings] = useState({
    annualCola: 1.72, // Updated default
    colaWhileEmployed: 2.1, // Updated default
    applyColaWhileEmployed: false, // New setting
    showAnnualCola: true, // Controls whether annual COLA is applied
    showColaWhileEmployed: true, // Controls whether COLA while employed is applied
    ssCola: 2.5, // Social Security COLA rate (default based on historical average)
    showSsCola: true, // Controls whether Social Security COLA is applied
    preRetirementGrowth: 8.0,
    postRetirementGrowth: 4.4,
    taxRate: 24,
    showAfterTax: false,
    discountRate: 3.0,
    endPensionYear: currentYear + 50,
    calculateNpv: true, // Renamed for consistency with GlobalSettingsForm
    ssaStartAge: 67 // Social Security start age (default 67)
  });
  
  const [expandedSections, setExpandedSections] = useState({
    bio: true,
    settings: false,
    scenarios: true,
    results: true,
    socialSecurity: true,
    chartOptions: false // Added for expandable chart display options
  });
  
  const [visibilitySettings, setVisibilitySettings] = useState({
    showTable: false,
    showSalary: true,
    showPension: true,
    showTsp: true,
    showTSPBalance: false,
    showSocialSecurity: true,
    showFERSSupplement: true, // Social Security Supplement visibility
    showTotal: true,
    showNPV: false,
    showIncomeDelta: false // New setting for income difference view
  });
  
  // Scenario-specific visibility settings
  const [scenarioVisibility, setScenarioVisibility] = useState({});

  // Load settings when user logs in
  useEffect(() => {
    const loadSettings = async () => {
      if (user && !user.isGuest) {
        try {
          const savedSettings = await getSettings(user.uid);
          if (savedSettings) {
            if (savedSettings.globalSettings) {
              setGlobalSettings(savedSettings.globalSettings);
            }
            if (savedSettings.expandedSections) {
              setExpandedSections(savedSettings.expandedSections);
            }
            if (savedSettings.visibilitySettings) {
              setVisibilitySettings(savedSettings.visibilitySettings);
            }
            if (savedSettings.scenarioVisibility) {
              setScenarioVisibility(savedSettings.scenarioVisibility);
            }
          }
        } catch (error) {
          console.error('Error loading settings:', error);
        }
      }
    };

    loadSettings();
  }, [user]);

  // Save settings to Firebase when they change (for logged-in users)
  useEffect(() => {
    const saveSettingsToFirebase = async () => {
      if (user && !user.isGuest) {
        try {
          const settingsData = {
            globalSettings,
            expandedSections,
            visibilitySettings,
            scenarioVisibility
          };
          await saveSettings(user.uid, settingsData);
        } catch (error) {
          console.error('Error saving settings:', error);
        }
      }
    };

    // Only save if we have a user (not on initial load when everything is defaults)
    if (user) {
      saveSettingsToFirebase();
    }
  }, [user, globalSettings, expandedSections, visibilitySettings, scenarioVisibility]);

  const handleGlobalSettingChange = (field, value) => {
    setGlobalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const toggleVisibilitySetting = (setting) => {
    setVisibilitySettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };
  
  // Initialize visibility settings for a scenario
  const initScenarioVisibility = (scenarioId) => {
    setScenarioVisibility(prev => {
      if (!prev[scenarioId]) {
        return {
          ...prev,
          [scenarioId]: {
            showSalary: true,
            showPension: true,
            showTsp: true,
            showFERSSupplement: true,
            showSocialSecurity: true,
            showTotal: true
          }
        };
      }
      return prev;
    });
  };
  
  // Toggle visibility for a specific data type in a specific scenario
  const toggleScenarioVisibility = (scenarioId, dataType) => {
    setScenarioVisibility(prev => {
      const scenarioSettings = prev[scenarioId] || {
        showSalary: true,
        showPension: true,
        showTsp: true,
        showFERSSupplement: true,
        showSocialSecurity: true,
        showTotal: true
      };
      
      return {
        ...prev,
        [scenarioId]: {
          ...scenarioSettings,
          [dataType]: !scenarioSettings[dataType]
        }
      };
    });
  };

  const value = {
    globalSettings,
    handleGlobalSettingChange,
    expandedSections,
    toggleSection,
    visibilitySettings,
    toggleVisibilitySetting,
    scenarioVisibility,
    initScenarioVisibility,
    toggleScenarioVisibility,
    showIncomeDelta: visibilitySettings.showIncomeDelta
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}